// @section 1 AccountWorkplanLens — open tasks rolled up to this account
//
// CRM+ERP merger Slice F. Surfaces every open hq_pm.tasks row whose polymorphic
// parent rolls up to the active company (deal / engagement / workstream /
// deliverable / company-direct). Reads from the aggregator's openTasks field
// directly — no extra fetch.
//
// In-line task transitions (Start / Done / Reopen) reuse the existing
// /api/hq/pm/tasks/{id}/transition endpoint. The list is the aggregator-cap of
// 50; if an account has more open work than that, the "+ N more" banner points
// to /pm where the full unscoped list lives.

import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  transitionTask, createTask, listUsers,
  listBdFacts, listBdAsks, listBdOffers, listBdOfferItems,
  type AccountAggregate, type AccountTask, type HqUser,
  type BdFact, type BdAsk, type BdOffer, type BdOfferItem,
} from '../../api';
import { useMeUser } from '../../lib/useMe';
import { useIsMobile } from '../../lib/useIsMobile';
import { FeedPills } from '../../components/MobileFeed';

type AssigneeFilter = 'mine' | 'all' | string; // string = a specific userId

export default function AccountWorkplanLens({ data }: { data: AccountAggregate }) {
  const me = useMeUser();
  const initial = data.openTasks ?? [];
  const [tasks, setTasks] = useState<AccountTask[]>(initial);
  const [users, setUsers] = useState<HqUser[]>([]);
  const [filter, setFilter] = useState<AssigneeFilter>('all');

  // Load users once for the assignee picker + filter chip labels.
  useEffect(() => {
    listUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  const userById = useMemo(() => {
    const m = new Map<string, HqUser>();
    for (const u of users) m.set(u.userId, u);
    return m;
  }, [users]);

  const filtered = useMemo(() => {
    if (filter === 'all') return tasks;
    if (filter === 'mine') return tasks.filter((t) => t.assigneeId && me && t.assigneeId === me.userId);
    return tasks.filter((t) => t.assigneeId === filter);
  }, [tasks, filter, me]);

  async function onTransition(id: string, status: string) {
    if (status === 'done' || status === 'dropped') {
      setTasks((cur) => cur.filter((t) => t.id !== id));
    } else {
      setTasks((cur) => cur.map((t) => (t.id === id ? { ...t, status } : t)));
    }
    try {
      await transitionTask(id, status);
    } catch (e) {
      setTasks(data.openTasks ?? []);
      alert(`Could not transition task: ${e}`);
    }
  }

  function onCreated(newTask: AccountTask) {
    // Prepend optimistically — next aggregator refetch (or a hard reload of
    // the account workspace) will reconcile with the server's ordering.
    setTasks((cur) => [newTask, ...cur]);
  }

  const total = data.openTasksCount;
  const truncated = total > tasks.length;

  // Layout: Pipeline lanes (Verify → Scope → Define) on top — the BD-derived
  // operator pipeline. Tasks list below — the literal hq_pm.tasks scoped to
  // this account. Both share the same conceptual scope (work on this account),
  // so they live on the same lens rather than behind a toggle.
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PipelineView data={data} />

      <TaskCreateForm data={data} users={users} onCreated={onCreated} />

      <Card
        title="Open tasks"
        subtitle={`${filtered.length} shown${filter !== 'all' ? ' (filtered)' : truncated ? ` of ${total}` : ''}`}
      >
        {/* Filter strip */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>All</FilterChip>
          {me && (
            <FilterChip active={filter === 'mine'} onClick={() => setFilter('mine')}>
              Mine ({tasks.filter((t) => t.assigneeId === me.userId).length})
            </FilterChip>
          )}
          {users
            .filter((u) => !me || u.userId !== me.userId)
            .map((u) => (
              <FilterChip
                key={u.userId}
                active={filter === u.userId}
                onClick={() => setFilter(u.userId)}
              >
                {u.displayName.split(' ')[0] || u.email.split('@')[0]} ({tasks.filter((t) => t.assigneeId === u.userId).length})
              </FilterChip>
            ))}
        </div>

        {filtered.length === 0 ? (
          <em style={{ color: '#6b7a90', fontSize: 13 }}>
            {tasks.length === 0
              ? "No open tasks on this account. Use the form above to add one."
              : 'No tasks match this filter.'}
          </em>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {filtered.map((t) => (
              <TaskRow key={t.id} task={t} assignee={t.assigneeId ? userById.get(t.assigneeId) : undefined} onTransition={onTransition} />
            ))}
          </ul>
        )}
        {truncated && filter === 'all' && (
          <div style={{ marginTop: 10, fontSize: 11, color: '#6b7a90', textAlign: 'center' }}>
            Showing first 50 — the global <a href="/pm">Projects</a> view has the rest.
          </div>
        )}
      </Card>
    </div>
  );
}

// @section 2 PipelineView — three-stage operator pipeline (READ-ONLY SPIKE)
//
// Reframes BD entities as Adam's behind-the-scenes execution pipeline:
//   Verify   — facts that need confirmation in client systems
//   Scope    — open asks that need diagnostic work
//   Define   — draft offers being built (with line items pinned to asks)
//
// All lane assignments are INFERRED from existing data — no schema change:
//   Verify = facts where source is null/empty or starts with "meeting:"
//            (claimed by the client but not confirmed by Adam)
//   Scope  = asks where status='open'
//   Define = offers where status='draft'
//
// Pure visual. No write actions, no mutations, no API additions. If the lane
// model survives Adam's review, the next pass adds writes + (maybe) explicit
// workplan_stage tagging.

type LaneKey = 'verify' | 'scope' | 'define';
type Focused = { lane: LaneKey; id: string } | null;

function PipelineView({ data }: { data: AccountAggregate }) {
  const cid = data.company.id;
  // @block 2.1 Phone: the 3-lane Verify/Scope/Define board becomes a segmented
  // picker (one lane at a time) instead of three stacked lanes.
  const isMobile = useIsMobile();
  const [mobileLane, setMobileLane] = useState<'verify' | 'scope' | 'define'>('verify');
  const [facts, setFacts] = useState<BdFact[] | null>(null);
  const [asks, setAsks] = useState<BdAsk[] | null>(null);
  const [offers, setOffers] = useState<BdOffer[] | null>(null);
  const [offerItems, setOfferItems] = useState<Record<string, BdOfferItem[]>>({});
  const [err, setErr] = useState<string | null>(null);

  // "One at a time" focus mode — clicking any card takes over the pipeline
  // area with an in-depth view of that one card + nav arrows to walk through
  // the lane. Esc / ← Back returns to the three-lane summary.
  const [focused, setFocused] = useState<Focused>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listBdFacts(cid), listBdAsks(cid), listBdOffers({ companyId: cid })])
      .then(async ([f, a, o]) => {
        if (cancelled) return;
        setFacts(f); setAsks(a); setOffers(o);
        // Fetch items for every draft offer so the Define cards can show
        // line-item counts + "addresses ask" back-references inline.
        const drafts = o.filter((of) => of.status === 'draft');
        const entries = await Promise.all(
          drafts.map((d) => listBdOfferItems(d.id).then((items) => [d.id, items] as const)),
        );
        if (!cancelled) setOfferItems(Object.fromEntries(entries));
      })
      .catch((e) => !cancelled && setErr(String(e)));
    return () => { cancelled = true; };
  }, [cid]);

  // ── Derived values via useMemo — these MUST come before any early return
  //    or hooks below them won't be called consistently across renders
  //    (Rules of Hooks). They safely handle the null-loading state.
  //
  // Inferred lane partitioning.
  const verifyFacts = useMemo(
    () => (facts ?? []).filter((f) => !f.source || f.source.toLowerCase().startsWith('meeting:')),
    [facts],
  );
  const scopeAsks = useMemo(
    () => (asks ?? []).filter((a) => a.status === 'open'),
    [asks],
  );
  const defineOffers = useMemo(
    () => (offers ?? []).filter((o) => o.status === 'draft'),
    [offers],
  );

  // Quick ask lookup so Define cards can show "addresses ask: <text>".
  const askById = useMemo(
    () => new Map((asks ?? []).map((a) => [a.id, a] as const)),
    [asks],
  );

  // Focus-mode lookup — resolves the focused id within its lane, plus prev/next
  // ids so the focus view can offer "← Prev / Next →" within the lane.
  const focusedItem = useMemo(() => {
    if (!focused) return null;
    const list: { id: string }[] =
      focused.lane === 'verify' ? verifyFacts
      : focused.lane === 'scope' ? scopeAsks
      : defineOffers;
    const idx = list.findIndex((x) => x.id === focused.id);
    if (idx === -1) return null;
    return {
      idx,
      total: list.length,
      prevId: idx > 0 ? list[idx - 1].id : null,
      nextId: idx < list.length - 1 ? list[idx + 1].id : null,
    };
  }, [focused, verifyFacts, scopeAsks, defineOffers]);

  // If the focused card disappears (e.g. underlying data changed and the
  // partition no longer includes it), close the focus view so we don't
  // render an empty detail panel.
  useEffect(() => {
    if (focused && !focusedItem) setFocused(null);
  }, [focused, focusedItem]);

  // Esc key closes the focus view; ← / → walk through the lane.
  useEffect(() => {
    if (!focused || !focusedItem) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setFocused(null); }
      else if (e.key === 'ArrowLeft' && focusedItem.prevId) {
        setFocused({ lane: focused.lane, id: focusedItem.prevId });
      }
      else if (e.key === 'ArrowRight' && focusedItem.nextId) {
        setFocused({ lane: focused.lane, id: focusedItem.nextId });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focused, focusedItem]);

  // ── Early returns — all hooks have already run above.
  if (err) return <div style={{ color: '#e87e7e' }}>{err}</div>;
  if (facts === null || asks === null || offers === null) {
    return <div style={{ color: '#6b7a90', fontSize: 13 }}>Loading pipeline…</div>;
  }

  // Quick deal name lookup for card subtitles — non-hook helper, safe here.
  const dealName = (id: string) => data.deals.find((d) => d.id === id)?.name ?? id.slice(0, 8);

  // ── Focus-mode render: one card in-depth, lanes hidden, with prev/next nav.
  if (focused && focusedItem) {
    return (
      <FocusView
        focused={focused}
        focusedItem={focusedItem}
        onClose={() => setFocused(null)}
        onJump={(id) => setFocused({ lane: focused.lane, id })}
        facts={facts}
        asks={asks}
        offers={offers}
        offerItems={offerItems}
        dealName={dealName}
        askById={askById}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <PipelineBanner facts={facts.length} verify={verifyFacts.length} scope={scopeAsks.length} define={defineOffers.length} />

      {isMobile && (
        <FeedPills
          options={[
            { key: 'verify', label: 'Verify', ct: verifyFacts.length },
            { key: 'scope',  label: 'Scope',  ct: scopeAsks.length },
            { key: 'define', label: 'Define', ct: defineOffers.length },
          ]}
          value={mobileLane}
          onChange={setMobileLane}
        />
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
        gap: 12,
        alignItems: 'start',
      }}>
        {(!isMobile || mobileLane === 'verify') && (
        <Lane title="Verify" hint="facts to confirm in client systems" count={verifyFacts.length}>
          {verifyFacts.length === 0 ? (
            <LaneEmpty>All captured facts have a non-meeting source — nothing to verify right now.</LaneEmpty>
          ) : (
            verifyFacts.map((f) => (
              <VerifyCard key={f.id} fact={f} dealName={dealName(f.dealId)}
                          onFocus={() => setFocused({ lane: 'verify', id: f.id })} />
            ))
          )}
        </Lane>
        )}

        {(!isMobile || mobileLane === 'scope') && (
        <Lane title="Scope" hint="asks to diagnose & gap-check" count={scopeAsks.length}>
          {scopeAsks.length === 0 ? (
            <LaneEmpty>No open asks. Capture one in the BD lens and it'll appear here.</LaneEmpty>
          ) : (
            scopeAsks.map((a) => (
              <ScopeCard key={a.id} ask={a} dealName={dealName(a.dealId)}
                         onFocus={() => setFocused({ lane: 'scope', id: a.id })} />
            ))
          )}
        </Lane>
        )}

        {(!isMobile || mobileLane === 'define') && (
        <Lane title="Define offer" hint="offers being built (drafts)" count={defineOffers.length}>
          {defineOffers.length === 0 ? (
            <LaneEmpty>No draft offers. Address an open ask from the BD lens to start one.</LaneEmpty>
          ) : (
            defineOffers.map((o) => (
              <DefineCard
                key={o.id}
                offer={o}
                items={offerItems[o.id] ?? []}
                dealName={dealName(o.dealId)}
                askById={askById}
                onFocus={() => setFocused({ lane: 'define', id: o.id })}
              />
            ))
          )}
        </Lane>
        )}
      </div>

      <div style={{
        padding: 10, border: '1px dashed #2a3346', borderRadius: 6,
        background: 'transparent', fontSize: 11, color: '#6b7a90', lineHeight: 1.6,
      }}>
        <strong style={{ color: '#a3b1c2' }}>Spike — read-only.</strong> Lane assignments are inferred:
        Verify = facts with no source or a <code>meeting:</code> source.
        Scope = asks with status=open.
        Define = offers with status=draft.
        Acting on a card means switching to the BD lens. If you like this lane model,
        the next pass adds inline actions (mark verified · log finding · send offer) and
        — optional — an explicit <code>workplan_stage</code> tag on the BD tables for per-card notes.
      </div>
    </div>
  );
}

function PipelineBanner({ facts, verify, scope, define }: { facts: number; verify: number; scope: number; define: number }) {
  return (
    <div style={{
      display: 'flex', gap: 24, padding: '10px 14px',
      border: '1px solid #232a37', borderRadius: 8, background: '#171c26',
      alignItems: 'center',
    }}>
      <div style={{ fontSize: 11, color: '#6b7a90' }}>
        From {facts} captured fact{facts === 1 ? '' : 's'} on this account:
      </div>
      <PipelineStat label="needs verification" value={verify} tone="#f0a96b" />
      <PipelineStat label="open asks to scope" value={scope} tone="#7eb6e8" />
      <PipelineStat label="draft offers" value={define} tone="#a08fd8" />
    </div>
  );
}

function PipelineStat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontSize: 18, fontWeight: 600, color: value === 0 ? '#6b7a90' : tone, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
      <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#6b7a90' }}>
        {label}
      </span>
    </div>
  );
}

function Lane({ title, hint, count, children }: { title: string; hint: string; count: number; children: React.ReactNode }) {
  return (
    <section style={{
      display: 'flex', flexDirection: 'column', gap: 8,
      padding: 12, border: '1px solid #232a37', borderRadius: 8,
      background: '#171c26', minHeight: 200,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#e6e8ee', textTransform: 'uppercase', letterSpacing: 1.5 }}>
            {title}
          </div>
          <div style={{ fontSize: 10, color: '#6b7a90', marginTop: 2 }}>{hint}</div>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
          background: '#1d2330', color: '#a3b1c2',
        }}>
          {count}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
        {children}
      </div>
    </section>
  );
}

function LaneEmpty({ children }: { children: React.ReactNode }) {
  return <em style={{ fontSize: 12, color: '#6b7a90', padding: '12px 4px' }}>{children}</em>;
}

// @block 2.2 VerifyCard — a fact awaiting confirmation
function VerifyCard({ fact, dealName, onFocus }: { fact: BdFact; dealName: string; onFocus: () => void }) {
  const label = fact.key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
  const sourceText = !fact.source ? 'no source — manual entry'
    : fact.source.toLowerCase().startsWith('meeting:') ? `claimed in ${fact.source}`
    : fact.source;

  return (
    <div style={pipelineCardClickableStyle} onClick={onFocus} role="button" tabIndex={0}
         onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onFocus(); } }}>
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 1.5, color: '#f0a96b', fontWeight: 600 }}>
        ⚑ {label}
      </div>
      <div style={{ fontSize: 13, color: '#e6e8ee', fontWeight: 500, lineHeight: 1.3 }}>{fact.value}</div>
      <div style={{ fontSize: 10, color: '#6b7a90' }}>{sourceText}</div>
      <div style={{ fontSize: 10, color: '#6b7a90', borderTop: '1px solid #232a37', paddingTop: 6, marginTop: 2 }}>
        deal: {dealName}
      </div>
    </div>
  );
}

// @block 2.3 ScopeCard — an open ask awaiting diagnosis
function ScopeCard({ ask, dealName, onFocus }: { ask: BdAsk; dealName: string; onFocus: () => void }) {
  return (
    <div style={pipelineCardClickableStyle} onClick={onFocus} role="button" tabIndex={0}
         onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onFocus(); } }}>
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 1.5, color: '#7eb6e8', fontWeight: 600 }}>
        ❓ Ask
        {ask.priority && (
          <span style={{
            marginLeft: 6, padding: '0px 5px', borderRadius: 3,
            background: ask.priority === 'high' ? '#e87e7e22' : ask.priority === 'medium' ? '#f0c67422' : '#7eb6e822',
            color: ask.priority === 'high' ? '#e87e7e' : ask.priority === 'medium' ? '#f0c674' : '#7eb6e8',
          }}>
            {ask.priority}
          </span>
        )}
      </div>
      <div style={{ fontSize: 13, color: '#e6e8ee', fontWeight: 500, lineHeight: 1.3 }}>{ask.ask}</div>
      {ask.detail && <div style={{ fontSize: 11, color: '#a3b1c2' }}>{ask.detail}</div>}
      <div style={{ fontSize: 10, color: '#6b7a90', borderTop: '1px solid #232a37', paddingTop: 6, marginTop: 2 }}>
        deal: {dealName}
      </div>
    </div>
  );
}

// @block 2.4 DefineCard — a draft offer with its line items + addressed asks
function DefineCard({ offer, items, dealName, askById, onFocus }: {
  offer: BdOffer; items: BdOfferItem[]; dealName: string; askById: Map<string, BdAsk>; onFocus: () => void;
}) {
  const total = offer.totalCents ?? items.reduce((s, it) => s + it.lineTotalCents, 0);
  const addressedAsks = items
    .map((it) => it.addressesAskId ? askById.get(it.addressesAskId) : null)
    .filter((a): a is BdAsk => a !== null && a !== undefined);

  return (
    <div style={pipelineCardClickableStyle} onClick={onFocus} role="button" tabIndex={0}
         onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onFocus(); } }}>
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 1.5, color: '#a08fd8', fontWeight: 600 }}>
        📋 v{offer.version} draft
      </div>
      <div style={{ fontSize: 13, color: '#e6e8ee', fontWeight: 500, lineHeight: 1.3 }}>
        {offer.summary ?? <em style={{ color: '#6b7a90' }}>untitled draft</em>}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#a08fd8', fontVariantNumeric: 'tabular-nums' }}>
        {total > 0
          ? new Intl.NumberFormat(undefined, { style: 'currency', currency: offer.currency, maximumFractionDigits: 0 }).format(total / 100)
          : <span style={{ fontSize: 11, color: '#6b7a90', fontWeight: 400 }}>no pricing yet</span>}
        <span style={{ fontSize: 10, color: '#6b7a90', marginLeft: 6, fontWeight: 400 }}>
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </span>
      </div>
      {addressedAsks.length > 0 && (
        <div style={{ fontSize: 10, color: '#a3b1c2', lineHeight: 1.5 }}>
          <div style={{ color: '#6b7a90', textTransform: 'uppercase', letterSpacing: 1, fontSize: 9, marginBottom: 2 }}>
            addresses
          </div>
          {addressedAsks.slice(0, 3).map((a) => (
            <div key={a.id} style={{ paddingLeft: 8 }}>· {a.ask}</div>
          ))}
          {addressedAsks.length > 3 && (
            <div style={{ paddingLeft: 8, color: '#6b7a90' }}>+ {addressedAsks.length - 3} more</div>
          )}
        </div>
      )}
      <div style={{ fontSize: 10, color: '#6b7a90', borderTop: '1px solid #232a37', paddingTop: 6, marginTop: 2 }}>
        deal: {dealName}
      </div>
    </div>
  );
}

const pipelineCardStyle: CSSProperties = {
  padding: 10,
  border: '1px solid #232a37',
  borderRadius: 6,
  background: '#1d2330',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
// Clickable variant of pipelineCardStyle — hover affordance + cursor pointer.
// Used by the three lane cards once focus mode landed; identical visual except
// for cursor + hover, which most browsers render as the slight outline change.
const pipelineCardClickableStyle: CSSProperties = {
  ...pipelineCardStyle,
  cursor: 'pointer',
};

// @section 3 FocusView — "one at a time" in-depth view of a single card
//
// Takes over the pipeline area while the focused card is being inspected.
// Header strip: ← Back · lane label · "n of N" position · prev / next arrows.
// Esc, ← and → are wired at the parent level (PipelineView) so the in-depth
// view can be walked entirely from the keyboard.
//
// Each lane has its own detail panel (VerifyDetail / ScopeDetail / DefineDetail)
// that pulls extra context from the data already in memory — no extra round-
// trips. All read-only; clicking a related-context item that links elsewhere
// just changes focus to that item if it's also in a lane.

type FocusedItemPosition = {
  idx: number;
  total: number;
  prevId: string | null;
  nextId: string | null;
};

function FocusView({
  focused, focusedItem, onClose, onJump,
  facts, asks, offers, offerItems, dealName, askById,
}: {
  focused: { lane: LaneKey; id: string };
  focusedItem: FocusedItemPosition;
  onClose: () => void;
  onJump: (id: string) => void;
  facts: BdFact[];
  asks: BdAsk[];
  offers: BdOffer[];
  offerItems: Record<string, BdOfferItem[]>;
  dealName: (id: string) => string;
  askById: Map<string, BdAsk>;
}) {
  const laneMeta = focused.lane === 'verify' ? { label: 'Verify',       hint: 'fact to confirm',  tone: '#f0a96b' }
                  : focused.lane === 'scope'  ? { label: 'Scope',        hint: 'ask to diagnose',  tone: '#7eb6e8' }
                  :                              { label: 'Define offer', hint: 'offer to build',   tone: '#a08fd8' };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Header strip */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', borderRadius: 8,
        border: `1px solid ${laneMeta.tone}33`,
        background: '#171c26',
      }}>
        <button
          onClick={onClose}
          style={{
            fontSize: 12, padding: '5px 10px', border: '1px solid #2a3346',
            borderRadius: 5, background: 'transparent', color: '#a3b1c2',
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          ← Back to pipeline
        </button>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{
            fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5,
            color: laneMeta.tone, fontWeight: 600,
          }}>
            {laneMeta.label}
          </span>
          <span style={{ fontSize: 11, color: '#6b7a90' }}>
            {focusedItem.idx + 1} of {focusedItem.total} · {laneMeta.hint}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => focusedItem.prevId && onJump(focusedItem.prevId)}
            disabled={!focusedItem.prevId}
            style={navBtnStyle}
            title="Previous (←)"
          >
            ← Prev
          </button>
          <button
            onClick={() => focusedItem.nextId && onJump(focusedItem.nextId)}
            disabled={!focusedItem.nextId}
            style={navBtnStyle}
            title="Next (→)"
          >
            Next →
          </button>
        </div>
      </div>

      {/* Detail body — lane-specific */}
      {focused.lane === 'verify' && (() => {
        const fact = facts.find((f) => f.id === focused.id);
        if (!fact) return <FocusMissing onClose={onClose} />;
        return <VerifyDetail fact={fact} dealName={dealName(fact.dealId)}
                              relatedFacts={facts.filter((f) => f.id !== fact.id && f.companyId === fact.companyId)} />;
      })()}

      {focused.lane === 'scope' && (() => {
        const ask = asks.find((a) => a.id === focused.id);
        if (!ask) return <FocusMissing onClose={onClose} />;
        // Offers + items on the same deal — which line items address THIS ask?
        const dealOffers = offers.filter((o) => o.dealId === ask.dealId);
        const itemsAddressingThisAsk = dealOffers.flatMap((o) =>
          (offerItems[o.id] ?? [])
            .filter((it) => it.addressesAskId === ask.id)
            .map((it) => ({ item: it, offer: o }))
        );
        return <ScopeDetail
          ask={ask}
          dealName={dealName(ask.dealId)}
          dealOffers={dealOffers}
          itemsAddressingThisAsk={itemsAddressingThisAsk}
        />;
      })()}

      {focused.lane === 'define' && (() => {
        const offer = offers.find((o) => o.id === focused.id);
        if (!offer) return <FocusMissing onClose={onClose} />;
        const items = offerItems[offer.id] ?? [];
        // Open asks on this offer's deal — which are addressed by THIS offer
        // vs still un-addressed (opportunity to expand the offer).
        const dealAsks = asks.filter((a) => a.dealId === offer.dealId && a.status === 'open');
        const addressedAskIds = new Set(items.map((it) => it.addressesAskId).filter(Boolean));
        return <DefineDetail
          offer={offer}
          items={items}
          dealName={dealName(offer.dealId)}
          openAsksOnDeal={dealAsks}
          addressedAskIds={addressedAskIds as Set<string>}
          askById={askById}
        />;
      })()}

      {/* Spike disclaimer */}
      <div style={{
        padding: 10, border: '1px dashed #2a3346', borderRadius: 6,
        background: 'transparent', fontSize: 11, color: '#6b7a90', lineHeight: 1.6,
      }}>
        <strong style={{ color: '#a3b1c2' }}>Spike — read-only.</strong> In-depth view shows extra
        context pulled from the data already in memory. Inline actions (mark verified · log finding ·
        send offer) come in the next pass if this focus pattern survives review.
        Keyboard: <kbd>Esc</kbd> back · <kbd>←</kbd> / <kbd>→</kbd> walk lane.
      </div>
    </div>
  );
}

function FocusMissing({ onClose }: { onClose: () => void }) {
  return (
    <div style={{
      padding: 18, border: '1px solid #232a37', borderRadius: 8,
      background: '#171c26', textAlign: 'center', color: '#6b7a90',
    }}>
      The focused card is no longer in this lane. <button onClick={onClose}
        style={{ background: 'transparent', border: 0, color: '#7eb6e8', cursor: 'pointer', textDecoration: 'underline' }}>
        Back to pipeline
      </button>
    </div>
  );
}

// @block 3.1 VerifyDetail — in-depth view of a fact awaiting confirmation
function VerifyDetail({ fact, dealName, relatedFacts }: {
  fact: BdFact;
  dealName: string;
  relatedFacts: BdFact[];
}) {
  const label = fact.key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();
  const sourceText = !fact.source ? 'no source — manual entry'
    : fact.source.toLowerCase().startsWith('meeting:') ? `claimed in ${fact.source} (not yet confirmed)`
    : fact.source;

  // Most-recent five related facts on the same company — gives context
  // ("what else do we know about them") without overwhelming the view.
  const contextFacts = [...relatedFacts]
    .sort((a, b) => (b.updatedAt < a.updatedAt ? -1 : 1))
    .slice(0, 5);

  return (
    <div style={detailCardStyle}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#f0a96b', fontWeight: 600 }}>
        ⚑ {label}
      </div>
      <div style={{ fontSize: 24, color: '#e6e8ee', fontWeight: 500, lineHeight: 1.2 }}>{fact.value}</div>

      <div style={detailDividerStyle} />

      <DetailRow label="Source">
        <div>{sourceText}</div>
        {fact.sourceUrl && (
          <a href={fact.sourceUrl} target="_blank" rel="noreferrer" style={{ color: '#7eb6e8', fontSize: 12 }}>
            {fact.sourceUrl} ↗
          </a>
        )}
      </DetailRow>
      <DetailRow label="Deal">{dealName}</DetailRow>
      <DetailRow label="Captured">{new Date(fact.createdAt).toLocaleString()}</DetailRow>
      {fact.updatedAt !== fact.createdAt && (
        <DetailRow label="Last edited">{new Date(fact.updatedAt).toLocaleString()}</DetailRow>
      )}

      <div style={detailDividerStyle} />

      <DetailSectionTitle>Suggested verification</DetailSectionTitle>
      <ul style={detailListStyle}>
        <li>Cross-check via QBO if the fact is financial (revenue, headcount).</li>
        <li>Cross-check via LinkedIn / Apollo for people + headcount claims.</li>
        <li>Cross-check via website / Google Maps for location + service area.</li>
        <li>Update the source from <code>meeting:…</code> to the verified source once confirmed — the card will leave this lane automatically.</li>
      </ul>

      {contextFacts.length > 0 && (
        <>
          <div style={detailDividerStyle} />
          <DetailSectionTitle>Recent facts on this account ({contextFacts.length})</DetailSectionTitle>
          <ul style={detailListStyle}>
            {contextFacts.map((f) => (
              <li key={f.id}>
                <strong>{f.key.replace(/[_-]+/g, ' ')}</strong>: {f.value}
                <span style={{ color: '#6b7a90', marginLeft: 6 }}>· {f.source ?? 'manual'}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// @block 3.2 ScopeDetail — in-depth view of an open ask awaiting diagnosis
function ScopeDetail({ ask, dealName, dealOffers, itemsAddressingThisAsk }: {
  ask: BdAsk;
  dealName: string;
  dealOffers: BdOffer[];
  itemsAddressingThisAsk: { item: BdOfferItem; offer: BdOffer }[];
}) {
  return (
    <div style={detailCardStyle}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#7eb6e8', fontWeight: 600 }}>
        ❓ Open ask
        {ask.priority && (
          <span style={{
            marginLeft: 8, padding: '1px 6px', borderRadius: 3, fontSize: 9,
            background: ask.priority === 'high' ? '#e87e7e22' : ask.priority === 'medium' ? '#f0c67422' : '#7eb6e822',
            color: ask.priority === 'high' ? '#e87e7e' : ask.priority === 'medium' ? '#f0c674' : '#7eb6e8',
          }}>
            {ask.priority}
          </span>
        )}
      </div>
      <div style={{ fontSize: 22, color: '#e6e8ee', fontWeight: 500, lineHeight: 1.3 }}>
        "{ask.ask}"
      </div>
      {ask.detail && <div style={{ fontSize: 13, color: '#a3b1c2', lineHeight: 1.5 }}>{ask.detail}</div>}

      <div style={detailDividerStyle} />

      <DetailRow label="Deal">{dealName}</DetailRow>
      <DetailRow label="Captured">{new Date(ask.createdAt).toLocaleString()}</DetailRow>

      <div style={detailDividerStyle} />

      <DetailSectionTitle>Diagnostic checklist</DetailSectionTitle>
      <ul style={detailListStyle}>
        <li>What does this ask MEAN? The literal request often isn't the real problem.</li>
        <li>What would success look like for them? Get a measurable outcome.</li>
        <li>What's the gap between their current state + that outcome? Surface as findings.</li>
        <li>What's the simplest thing we could propose that closes the gap?</li>
        <li>Is there something they're NOT asking for that would move the needle more?</li>
      </ul>

      <div style={detailDividerStyle} />

      <DetailSectionTitle>
        Offers on this deal ({dealOffers.length} total · {itemsAddressingThisAsk.length} address this ask)
      </DetailSectionTitle>
      {dealOffers.length === 0 ? (
        <em style={{ color: '#6b7a90', fontSize: 12 }}>No offers drafted on this deal yet. Address the ask from the BD lens to start one.</em>
      ) : itemsAddressingThisAsk.length === 0 ? (
        <em style={{ color: '#6b7a90', fontSize: 12 }}>
          {dealOffers.length} offer{dealOffers.length === 1 ? '' : 's'} on this deal — none address this ask yet.
        </em>
      ) : (
        <ul style={detailListStyle}>
          {itemsAddressingThisAsk.map(({ item, offer }) => (
            <li key={item.id}>
              <strong>v{offer.version} ({offer.status})</strong> · {item.name}
              <span style={{ color: '#a3b1c2', marginLeft: 6 }}>
                · {item.qty} × ${(item.unitCents / 100).toLocaleString()}
                = ${(item.lineTotalCents / 100).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// @block 3.3 DefineDetail — in-depth view of a draft offer being built
function DefineDetail({ offer, items, dealName, openAsksOnDeal, addressedAskIds, askById }: {
  offer: BdOffer;
  items: BdOfferItem[];
  dealName: string;
  openAsksOnDeal: BdAsk[];
  addressedAskIds: Set<string>;
  askById: Map<string, BdAsk>;
}) {
  const total = offer.totalCents ?? items.reduce((s, it) => s + it.lineTotalCents, 0);
  const unaddressedAsks = openAsksOnDeal.filter((a) => !addressedAskIds.has(a.id));

  return (
    <div style={detailCardStyle}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#a08fd8', fontWeight: 600 }}>
        📋 v{offer.version} draft
      </div>
      <div style={{ fontSize: 22, color: '#e6e8ee', fontWeight: 500, lineHeight: 1.2 }}>
        {offer.summary ?? <em style={{ color: '#6b7a90' }}>untitled draft</em>}
      </div>
      <div style={{ fontSize: 28, fontWeight: 600, color: '#a08fd8', fontVariantNumeric: 'tabular-nums' }}>
        {total > 0
          ? new Intl.NumberFormat(undefined, { style: 'currency', currency: offer.currency, maximumFractionDigits: 0 }).format(total / 100)
          : <span style={{ fontSize: 14, color: '#6b7a90', fontWeight: 400 }}>no pricing yet</span>}
      </div>

      <div style={detailDividerStyle} />

      <DetailRow label="Deal">{dealName}</DetailRow>
      <DetailRow label="Created">{new Date(offer.createdAt).toLocaleString()}</DetailRow>
      <DetailRow label="Last updated">{new Date(offer.updatedAt).toLocaleString()}</DetailRow>
      {offer.body && (
        <div style={{ marginTop: 8 }}>
          <DetailSectionTitle>Body</DetailSectionTitle>
          <div style={{ fontSize: 13, color: '#a3b1c2', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {offer.body}
          </div>
        </div>
      )}

      <div style={detailDividerStyle} />

      <DetailSectionTitle>Line items ({items.length})</DetailSectionTitle>
      {items.length === 0 ? (
        <em style={{ color: '#6b7a90', fontSize: 12 }}>No line items yet.</em>
      ) : (
        <ul style={detailListStyle}>
          {items.map((it) => {
            const ask = it.addressesAskId ? askById.get(it.addressesAskId) : null;
            return (
              <li key={it.id}>
                <strong>{it.name}</strong>
                <span style={{ color: '#a3b1c2', marginLeft: 8 }}>
                  {it.qty} × ${(it.unitCents / 100).toLocaleString()}
                  = <strong style={{ color: '#e6e8ee' }}>${(it.lineTotalCents / 100).toLocaleString()}</strong>
                  {it.recurring && it.recurring !== 'one-time' && <> · {it.recurring}</>}
                </span>
                {ask && (
                  <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 2, paddingLeft: 12 }}>
                    ↳ addresses ask: "{ask.ask}"
                  </div>
                )}
                {it.description && (
                  <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 2, paddingLeft: 12 }}>{it.description}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {unaddressedAsks.length > 0 && (
        <>
          <div style={detailDividerStyle} />
          <DetailSectionTitle>
            Open asks on this deal not yet addressed ({unaddressedAsks.length})
          </DetailSectionTitle>
          <ul style={detailListStyle}>
            {unaddressedAsks.map((a) => (
              <li key={a.id}>
                "{a.ask}"
                {a.priority && (
                  <span style={{
                    marginLeft: 6, fontSize: 9, padding: '1px 5px', borderRadius: 3,
                    background: a.priority === 'high' ? '#e87e7e22' : a.priority === 'medium' ? '#f0c67422' : '#7eb6e822',
                    color: a.priority === 'high' ? '#e87e7e' : a.priority === 'medium' ? '#f0c674' : '#7eb6e8',
                  }}>
                    {a.priority}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 6, fontStyle: 'italic' }}>
            Each unaddressed ask is a potential line item — consider whether the offer should cover them
            or if they're out of scope.
          </div>
        </>
      )}
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, fontSize: 13, padding: '4px 0' }}>
      <span style={{
        fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5,
        color: '#6b7a90', minWidth: 100, paddingTop: 2,
      }}>
        {label}
      </span>
      <div style={{ flex: 1, color: '#e6e8ee' }}>{children}</div>
    </div>
  );
}

function DetailSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 10, textTransform: 'uppercase', letterSpacing: 2,
      color: '#a3b1c2', fontWeight: 600, marginBottom: 6,
    }}>
      {children}
    </div>
  );
}

const detailCardStyle: CSSProperties = {
  padding: '20px 24px',
  border: '1px solid #232a37',
  borderRadius: 8,
  background: '#171c26',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const detailDividerStyle: CSSProperties = {
  height: 1,
  background: '#232a37',
  margin: '14px 0 6px',
};
const detailListStyle: CSSProperties = {
  margin: 0,
  paddingLeft: 18,
  fontSize: 13,
  color: '#a3b1c2',
  lineHeight: 1.7,
};
const navBtnStyle: CSSProperties = {
  fontSize: 11,
  padding: '5px 10px',
  border: '1px solid #2a3346',
  borderRadius: 5,
  background: 'transparent',
  color: '#a3b1c2',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

// @section 4 Inline task create form
function TaskCreateForm({ data, users, onCreated }: {
  data: AccountAggregate;
  users: HqUser[];
  onCreated: (t: AccountTask) => void;
}) {
  const me = useMeUser();
  const [title, setTitle] = useState('');
  const [parent, setParent] = useState('');         // "<parentType>:<parentId>" or "company"
  const [priority, setPriority] = useState<2 | 1 | 3>(2);
  const [assigneeId, setAssigneeId] = useState<string>('');
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Default assignee = me (once /me resolves and we haven't started typing).
  useEffect(() => {
    if (me && !assigneeId) setAssigneeId(me.userId);
  }, [me, assigneeId]);

  // Parent options: deals on this account + engagements + "the account itself".
  const parentOpts = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    opts.push({ value: 'company', label: `${data.company.name} (account-level)` });
    for (const d of data.deals.filter((d) => !d.closedAt)) {
      opts.push({ value: `deal:${d.id}`, label: `Deal · ${d.name}` });
    }
    for (const e of data.engagements) {
      opts.push({ value: `engagement:${e.id}`, label: `Engagement · ${e.name}` });
    }
    return opts;
  }, [data]);

  // Default parent = first available, on mount.
  useEffect(() => {
    if (!parent && parentOpts.length > 0) setParent(parentOpts[0].value);
  }, [parentOpts, parent]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !parent) return;
    setBusy(true);
    setErr(null);
    try {
      const [parentType, parentId] = parent === 'company'
        ? ['company', data.company.id]
        : parent.split(':');
      const t = await createTask({
        title: title.trim(),
        parentType,
        parentId,
        priority,
        dueDate: dueDate || undefined,
        assigneeId: assigneeId || undefined,
      });
      // Convert PM Task → AccountTask shape for optimistic insert.
      onCreated({
        id: t.id,
        title: t.title,
        status: t.status,
        priority: t.priority,
        dueDate: t.dueDate ?? null,
        parentType,
        parentId,
        assigneeId: assigneeId || null,
        createdAt: new Date().toISOString(),
      });
      setTitle(''); setDueDate('');
    } catch (ex) {
      setErr(String(ex));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1.5, color: '#a3b1c2' }}>+ Add task</h3>
        <span style={{ fontSize: 11, color: '#6b7a90' }}>shows up here + on the assignee's My Day</span>
      </div>
      <form onSubmit={submit} style={{ display: 'grid', gap: 8 }}>
        <input
          placeholder="Task title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select value={parent} onChange={(e) => setParent(e.target.value)} style={{ flex: 1, minWidth: 200 }}>
            {parentOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} style={{ width: 180 }}>
            <option value="">(unassigned)</option>
            {users.map((u) => (
              <option key={u.userId} value={u.userId}>
                {u.displayName || u.email}{me && u.userId === me.userId ? ' (me)' : ''}
              </option>
            ))}
          </select>
          <select value={priority} onChange={(e) => setPriority(Number(e.target.value) as 1 | 2 | 3)} style={{ width: 90 }}>
            <option value={1}>P1</option>
            <option value={2}>P2</option>
            <option value={3}>P3</option>
          </select>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            style={{ width: 150 }}
          />
          <button type="submit" disabled={busy || !title.trim() || !parent}>
            {busy ? 'Adding…' : 'Add'}
          </button>
        </div>
        {err && <div style={{ color: '#e87e7e', fontSize: 12 }}>{err}</div>}
      </form>
    </section>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontSize: 11,
        padding: '3px 10px',
        border: `1px solid ${active ? '#7eb6e8' : '#2a3346'}`,
        borderRadius: 99,
        background: active ? '#7eb6e822' : 'transparent',
        color: active ? '#e6e8ee' : '#a3b1c2',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function TaskRow({ task, assignee, onTransition }: {
  task: AccountTask;
  assignee?: HqUser;
  onTransition: (id: string, status: string) => void;
}) {
  const tone = task.priority === 1 ? '#e87e7e' : task.priority === 2 ? '#f0c674' : '#a3b1c2';
  const isStarted = task.status === 'in_progress';
  const overdue = task.dueDate && task.dueDate < new Date().toISOString().slice(0, 10);

  return (
    <li style={rowStyle}>
      <span style={{
        fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1,
        padding: '2px 6px', borderRadius: 3, background: `${tone}22`, color: tone, flexShrink: 0,
      }}>
        P{task.priority}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13 }}>
          {isStarted && <span style={{ color: '#7eb6e8', marginRight: 6 }}>▶</span>}
          {task.title}
        </div>
        <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ textTransform: 'uppercase', letterSpacing: 1 }}>{task.parentType}</span>
          {task.dueDate && (
            <span style={{ color: overdue ? '#e87e7e' : '#6b7a90' }}>
              due {task.dueDate}{overdue ? ' · overdue' : ''}
            </span>
          )}
          <AssigneeChip assignee={assignee} taskHasAssignee={!!task.assigneeId} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {task.status === 'todo' && (
          <button onClick={() => onTransition(task.id, 'in_progress')} style={btnSm}>Start</button>
        )}
        <button
          onClick={() => onTransition(task.id, 'done')}
          style={{ ...btnSm, color: '#6ec78a', borderColor: '#6ec78a44' }}
        >
          Done
        </button>
      </div>
    </li>
  );
}

function AssigneeChip({ assignee, taskHasAssignee }: { assignee?: HqUser; taskHasAssignee: boolean }) {
  if (assignee) {
    const initials = assignee.displayName
      ? assignee.displayName.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()
      : assignee.email.slice(0, 2).toUpperCase();
    return (
      <span title={assignee.displayName || assignee.email} style={{
        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 99,
        background: '#7eb6e822', color: '#7eb6e8',
      }}>
        @{initials}
      </span>
    );
  }
  if (taskHasAssignee) {
    return <span style={{ fontSize: 10, color: '#6b7a90' }}>· assigned</span>;
  }
  return <span style={{ fontSize: 10, color: '#6b7a90', fontStyle: 'italic' }}>· unassigned</span>;
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1.5, color: '#a3b1c2' }}>{title}</h3>
        {subtitle && <span style={{ fontSize: 11, color: '#6b7a90' }}>{subtitle}</span>}
      </div>
      {children}
    </section>
  );
}

const cardStyle: CSSProperties = {
  border: '1px solid #232a37',
  borderRadius: 8,
  padding: 14,
  background: '#171c26',
};
const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '8px 0',
  borderTop: '1px solid #232a37',
};
const btnSm: CSSProperties = {
  fontSize: 11,
  padding: '3px 8px',
  border: '1px solid #2a3346',
  borderRadius: 4,
  background: 'transparent',
  color: '#a3b1c2',
  cursor: 'pointer',
};
